import React, { useState, useEffect, useRef } from "react";
import { formatAadhaar, cleanAadhaar } from "../utils/validation";
import { useParams } from "react-router-dom";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const TherapistPage = () => {
  // Get the therapist ID from URL parameters
  const { id } = useParams();
  const [therapist, setTherapist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  // Photo upload state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [photoError, setPhotoError] = useState(null);
  const fileInputRef = useRef(null);

  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 4000);
  };

  useEffect(() => {
    const fetchTherapist = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/v1/therapists/${id}`,
        );

        setTherapist({
          name: response.data.name,
          therapistId: response.data.id,
          // keep both raw ISO values (for editing) and formatted strings (for display)
          date_of_birth_raw: response.data.date_of_birth,
          dob: response.data.date_of_birth
            ? new Date(response.data.date_of_birth).toLocaleDateString(
                "en-US",
                {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                },
              )
            : "",
          gender: response.data.gender,
          religion: response.data.religion,
          caste: response.data.caste,
          mobile: response.data.mobile_number,
          email: response.data.email || "Not provided",
          address: response.data.address,
          aadhar_raw: response.data.aadhar_number,
          aadhar: formatAadhaar(response.data.aadhar_number),
          bloodGroup: response.data.blood_group,
          category: response.data.category,
          rciNumber: response.data.rci_number,
          rci_renewal_date_raw: response.data.rci_renewal_date,
          rciRenewalDate: response.data.rci_renewal_date
            ? new Date(response.data.rci_renewal_date).toLocaleDateString(
                "en-US",
                {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                },
              )
            : "",
          qualifications: response.data.qualifications_details,
          specialization: response.data.specialization || "Not specified",
          // prefer stored photo_url, otherwise ui-avatars fallback
          photoUrl:
            response.data.photo_url ||
            `https://eu.ui-avatars.com/api/?name=${(response.data.name || "").replace(" ", "+")}&size=250`,
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching therapist:", error);
        setLoading(false);
      }
    };

    fetchTherapist();
  }, [id]);

  // File selection handler
  const handlePhotoSelect = (e) => {
    setPhotoError(null);
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg"];
    if (!allowed.includes(file.type)) {
      setPhotoError("Only PNG and JPG images are allowed.");
      return;
    }
    // optional size limit (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError("File too large. Max 5MB allowed.");
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  // Upload handler
  const uploadPhoto = async () => {
    if (!photoFile) {
      setPhotoError("No file selected.");
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setPhotoError(null);
    try {
      const formData = new FormData();
      formData.append("file", photoFile);

      const headers = {};
      const token = localStorage.getItem("token");
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const resp = await axios.post(
        `${API_BASE_URL}/api/v1/therapists/${id}/photo`,
        formData,
        {
          headers,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const pct = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              setUploadProgress(pct);
            }
          },
          validateStatus: (status) => status < 500,
        },
      );

      if (resp.status >= 400) {
        const serverMsg =
          resp.data &&
          (resp.data.detail || resp.data.message || JSON.stringify(resp.data));
        throw new Error(
          serverMsg || `Upload failed with status ${resp.status}`,
        );
      }

      const newUrl = resp.data.photo_url || resp.data.url || resp.data.photoUrl;
      setTherapist((prev) => ({ ...prev, photoUrl: newUrl || prev.photoUrl }));
      setPhotoFile(null);
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
      }
      setPhotoPreview(null);
      setUploadProgress(0);
      showToast("Photo uploaded successfully.");
    } catch (err) {
      console.error("Photo upload failed", err, err.response?.data || "");
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        (typeof err.response?.data === "string"
          ? err.response.data
          : err.response?.data
            ? JSON.stringify(err.response.data)
            : err.message) ||
        "Upload failed. Please try again.";
      setPhotoError(errorMessage);
      showToast(`Failed to upload photo: ${errorMessage}`, "error");
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoDelete = async () => {
    if (photoPreview) {
      try {
        URL.revokeObjectURL(photoPreview);
      } catch (err) { }
      setPhotoPreview(null);
      setPhotoFile(null);
      if (fileInputRef.current) fileInputRef.current.value = null;
      return;
    }

    if (!therapist?.photoUrl && !therapist?.photo_url) {
      return;
    }

    if (!window.confirm("Are you sure you want to delete this therapist's photo?")) {
      return;
    }

    try {
      const headers = {};
      const token = localStorage.getItem("token");
      if (token) headers["Authorization"] = `Bearer ${token}`;

      await axios.delete(`${API_BASE_URL}/api/v1/therapists/${id}/photo`, { headers });

      setTherapist((prev) => ({ ...(prev || {}), photoUrl: null }));
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
      }
      setPhotoFile(null);
      setPhotoPreview(null);
      setUploadProgress(0);
      showToast("Photo deleted successfully.");
    } catch (err) {
      console.error("Photo delete failed", err, err.response?.data || "");
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        (typeof err.response?.data === "string"
          ? err.response.data
          : err.response?.data
            ? JSON.stringify(err.response.data)
            : err.message) ||
        "Delete failed. Please try again.";
      setPhotoError(errorMessage);
      showToast(`Failed to delete photo: ${errorMessage}`, "error");
    }
  };

  const cancelPhotoSelection = () => {
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoError(null);
    setUploadProgress(0);
  };

  // Function to handle edit mode toggle
  const handleEditToggle = () => {
    if (!isEditing) {
      // Enter edit mode - populate form with current data
      setEditFormData({
        name: therapist.name,
        mobile_number: therapist.mobile,
        email: therapist.email,
        address: therapist.address,
        qualifications_details: therapist.qualifications,
        rci_number: therapist.rciNumber,
        rci_renewal_date:
          therapist.rci_renewal_date_raw || therapist.rciRenewalDate,
        blood_group: therapist.bloodGroup,
        category: therapist.category,
        aadhar_number: therapist.aadhar_raw
          ? formatAadhaar(therapist.aadhar_raw)
          : therapist.aadhar,
        religion: therapist.religion,
        caste: therapist.caste,
        gender: therapist.gender,
        date_of_birth: therapist.date_of_birth_raw || therapist.dob,
        specialization: therapist.specialization,
      });
    }
    setIsEditing(!isEditing);
  };

  // Function to save edited data
  const handleSaveEdit = async () => {
    try {
      const payload = {
        ...editFormData,
        aadhar_number: editFormData.aadhar_number
          ? String(editFormData.aadhar_number).replace(/\s+/g, "")
          : undefined,
        date_of_birth: editFormData.date_of_birth || null,
        rci_renewal_date: editFormData.rci_renewal_date || null,
      };

      const token = localStorage.getItem("token");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const response = await axios.put(
        `${API_BASE_URL}/api/v1/therapists/${id}`,
        payload,
        config
      );

      if (response.status === 200) {
        setTherapist((prev) => ({
          ...prev,
          name: editFormData.name,
          mobile: editFormData.mobile_number,
          email: editFormData.email,
          address: editFormData.address,
          qualifications: editFormData.qualifications_details,
          rciNumber: editFormData.rci_number,
          rci_renewal_date_raw: editFormData.rci_renewal_date,
          rciRenewalDate: editFormData.rci_renewal_date
            ? new Date(editFormData.rci_renewal_date).toLocaleDateString(
                "en-US",
                { year: "numeric", month: "long", day: "numeric" },
              )
            : "",
          bloodGroup: editFormData.blood_group,
          category: editFormData.category,
          aadhar_raw: editFormData.aadhar_number
            ? String(editFormData.aadhar_number).replace(/\s+/g, "")
            : undefined,
          aadhar: editFormData.aadhar_number
            ? formatAadhaar(editFormData.aadhar_number)
            : prev.aadhar,
          religion: editFormData.religion,
          caste: editFormData.caste,
          gender: editFormData.gender,
          date_of_birth_raw: editFormData.date_of_birth,
          dob: editFormData.date_of_birth
            ? new Date(editFormData.date_of_birth).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : prev.dob,
          specialization: editFormData.specialization,
        }));

        setIsEditing(false);
        showToast("Therapist details updated successfully!");
      }
    } catch (error) {
      console.error("Error updating therapist:", error);
      showToast("Failed to update therapist details. Please try again.", "error");
    }
  };

  // Function to cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({});
  };

  if (loading) {
    return (
      <div className="min-h-screen max-lg:min-h-[100dvh] w-full flex flex-col items-center justify-center bg-[#f7f7f7]">
        <div className="text-2xl text-[#E38B52]">
          Loading therapist information...
        </div>
      </div>
    );
  }

  if (!therapist) {
    return (
      <div className="min-h-screen max-lg:min-h-[100dvh] w-full flex flex-col items-center justify-center bg-[#f7f7f7]">
        <div className="text-2xl text-[#E38B52]">Therapist not found.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen max-lg:min-h-[100dvh] w-full flex flex-col items-center bg-[#f7f7f7] relative overflow-hidden py-20 max-lg:py-0 max-lg:pb-[calc(40px+env(safe-area-inset-bottom))] max-lg:pt-0 max-lg:overflow-x-hidden">

      {/* Mobile Sticky Header */}
      <div className="hidden max-lg:flex sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 py-3 items-center shadow-sm">
        <button
          onClick={() => window.history.back()}
          className="flex items-center gap-2 text-[#6F6C90] hover:text-[#170F49] transition-colors bg-transparent p-0 m-0 border-none shadow-none"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="font-medium text-sm">Back</span>
        </button>
      </div>

      {/* Back button */}
      <button
        onClick={() => window.history.back()}
        className="absolute top-8 left-8 max-lg:hidden bg-white/30 backdrop-blur-xl rounded-2xl shadow-xl p-3 border border-white/20 hover:-translate-y-1 transition-all duration-200 flex items-center gap-2 z-10"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* Animated background blobs */}
      <div className="absolute top-0 -left-40 w-[600px] h-[500px] bg-[#E38B52] rounded-full mix-blend-multiply filter blur-2xl opacity-30 animate-float z-0" />
      <div className="absolute -bottom-32 right-40 w-[600px] h-[600px] bg-[#E38B52] rounded-full mix-blend-multiply filter blur-2xl opacity-40 animate-float animation-delay-3000 z-0" />
      <div className="absolute top-1/2 left-1/2 w-[500px] h-[500px] bg-[#E38B52] rounded-full mix-blend-multiply filter blur-2xl opacity-40 animate-float animation-delay-5000 z-0" />
      <div className="absolute top-0 -left-40 w-[500px] h-[600px] bg-[#E38B52] rounded-full mix-blend-multiply filter blur-2xl opacity-30 animate-float animation-delay-7000 z-0" />

      <div className="w-[90%] max-w-[1200px] mx-4 flex-1 flex flex-col">
        <h1 className="text-3xl font-bold text-[#170F49] mb-8 max-md:mb-5 text-center font-baskervville">
          Therapist Information
        </h1>

        {/* Main content container */}
        <div className="relative bg-white/30 backdrop-blur-xl rounded-3xl shadow-xl p-8 md:p-12 max-md:p-3 border border-white/20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-md:gap-4 md:gap-8 max-md:gap-4">
            {/* Basic Information Section */}
            <div className="col-span-full">
              <h2 className="text-xl font-semibold text-[#170F49] mb-4">
                Personal Information
              </h2>
              <div className="flex flex-col md:flex-row gap-6 max-md:gap-4 p-4 md:p-6 bg-white/50 rounded-2xl">
                {/* Therapist Photo */}
                <div className="flex flex-col items-center gap-4">
                  <div className="w-40 h-40 rounded-2xl overflow-hidden border-4 border-white/50 shadow-xl">
                    <img
                      src={
                        photoPreview ||
                        therapist?.photoUrl ||
                        therapist?.photo_url ||
                        "https://placehold.co/200x200/EFEFEF/AAAAAA?text=No+Photo"
                      }
                      alt="Therapist"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePhotoSelect}
                    accept="image/png, image/jpeg"
                    style={{ display: "none" }}
                  />

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2.5 bg-white rounded-lg border border-[#E38B52]/30 hover:bg-[#E38B52] hover:border-[#E38B52] transition-all duration-200 shadow-md group"
                      title="Upload Photo"
                      type="button"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600 group-hover:text-white transition-colors duration-200">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                    </button>

                    {(therapist?.photoUrl || therapist?.photo_url || photoPreview) && (
                      <button
                        onClick={handlePhotoDelete}
                        className="p-2.5 bg-white rounded-lg border border-red-500/30 hover:bg-red-500 hover:border-red-500 transition-all duration-200 shadow-md group"
                        title="Delete Photo"
                        type="button"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600 group-hover:text-white transition-colors duration-200">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {photoFile && (
                    <button
                      onClick={uploadPhoto}
                      disabled={uploading}
                      className={`mt-2 px-4 py-2 text-white text-sm rounded-xl transition-all duration-200 shadow-md flex items-center justify-center gap-2 ${uploading ? "bg-gray-400 cursor-not-allowed" : "bg-green-500 hover:bg-green-600"}`}
                      type="button"
                    >
                      {uploading ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                          </svg>
                          Uploading...
                        </>
                      ) : (
                        "Upload Photo"
                      )}
                    </button>
                  )}

                  {photoError && (
                    <p className="text-sm text-red-500 text-center max-w-xs">{photoError}</p>
                  )}
                </div>

                {/* Therapist Details */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-md:gap-4 pl-0 md:pl-12">
                  <div>
                    <p className="text-sm text-[#6F6C90]">Full Name</p>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.name || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            name: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] focus:border-transparent bg-white/80"
                      />
                    ) : (
                      <p className="text-[#170F49] font-medium">
                        {therapist.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-[#6F6C90]">Therapist ID</p>
                    <p className="text-[#170F49] font-medium">
                      #{therapist.therapistId}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#6F6C90]">Date of Birth</p>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editFormData.date_of_birth || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            date_of_birth: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] focus:border-transparent bg-white/80"
                      />
                    ) : (
                      <p className="text-[#170F49] font-medium">
                        {therapist.dob}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-[#6F6C90]">Gender</p>
                    {isEditing ? (
                      <select
                        value={editFormData.gender || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            gender: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] focus:border-transparent bg-white/80"
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    ) : (
                      <p className="text-[#170F49] font-medium">
                        {therapist.gender}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-[#6F6C90]">Religion</p>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.religion || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            religion: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] focus:border-transparent bg-white/80"
                      />
                    ) : (
                      <p className="text-[#170F49] font-medium">
                        {therapist.religion}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-[#6F6C90]">Caste</p>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.caste || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            caste: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] focus:border-transparent bg-white/80"
                      />
                    ) : (
                      <p className="text-[#170F49] font-medium">
                        {therapist.caste}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="col-span-full">
              <h2 className="text-xl font-semibold text-[#170F49] mb-4">
                Contact Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-md:gap-4 p-6 bg-white/50 rounded-2xl">
                <div>
                  <p className="text-sm text-[#6F6C90]">Mobile Number</p>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editFormData.mobile_number || ""}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          mobile_number: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] focus:border-transparent bg-white/80"
                    />
                  ) : (
                    <p className="text-[#170F49] font-medium">
                      {therapist.mobile}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-[#6F6C90]">Email</p>
                  {isEditing ? (
                    <input
                      type="email"
                      value={editFormData.email || ""}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          email: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] focus:border-transparent bg-white/80"
                    />
                  ) : (
                    <p className="text-[#170F49] font-medium">
                      {therapist.email}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-[#6F6C90]">Address</p>
                  {isEditing ? (
                    <textarea
                      value={editFormData.address || ""}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          address: e.target.value,
                        })
                      }
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] focus:border-transparent bg-white/80 resize-none"
                    />
                  ) : (
                    <p className="text-[#170F49] font-medium">
                      {therapist.address}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Identification Details Section */}
            <div className="col-span-full">
              <h2 className="text-xl font-semibold text-[#170F49] mb-4">
                Identification Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-md:gap-4 p-6 bg-white/50 rounded-2xl">
                <div>
                  <p className="text-sm text-[#6F6C90]">Aadhar Number</p>
                  <p className="text-[#170F49] font-medium">
                    {therapist.aadhar}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#6F6C90]">Blood Group</p>
                  <p className="text-[#170F49] font-medium">
                    {therapist.bloodGroup}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#6F6C90]">Category</p>
                  <p className="text-[#170F49] font-medium">
                    {therapist.category}
                  </p>
                </div>
              </div>
            </div>

            {/* Professional Information Section */}
            <div className="col-span-full">
              <h2 className="text-xl font-semibold text-[#170F49] mb-4">
                Professional Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-md:gap-4 p-6 bg-white/50 rounded-2xl">
                <div>
                  <p className="text-sm text-[#6F6C90]">RCI Number</p>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editFormData.rci_number || ""}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          rci_number: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] focus:border-transparent bg-white/80"
                    />
                  ) : (
                    <p className="text-[#170F49] font-medium">
                      {therapist.rciNumber}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-[#6F6C90]">RCI Renewal Date</p>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editFormData.rci_renewal_date || ""}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          rci_renewal_date: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] focus:border-transparent bg-white/80"
                    />
                  ) : (
                    <p className="text-[#170F49] font-medium">
                      {therapist.rciRenewalDate}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-[#6F6C90]">Specialization</p>
                  {isEditing ? (
                    <select
                      value={editFormData.specialization || ""}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          specialization: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] focus:border-transparent bg-white/80"
                    >
                      <option value="">Select Specialization</option>
                      <option value="Speech Therapy">Speech Therapy</option>
                      <option value="Occupational Therapy">
                        Occupational Therapy
                      </option>
                      <option value="Physiotherapy">Physiotherapy</option>
                      <option value="Behavioral Therapy">
                        Behavioral Therapy
                      </option>
                      <option value="Special Education">
                        Special Education
                      </option>
                      <option value="Other">Other</option>
                    </select>
                  ) : (
                    <p className="text-[#170F49] font-medium">
                      {therapist.specialization}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-[#6F6C90]">
                    Qualifications Details
                  </p>
                  {isEditing ? (
                    <textarea
                      value={editFormData.qualifications_details || ""}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          qualifications_details: e.target.value,
                        })
                      }
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] focus:border-transparent bg-white/80 resize-none"
                    />
                  ) : (
                    <p className="text-[#170F49] font-medium">
                      {therapist.qualifications}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="col-span-full flex gap-4 mt-6">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSaveEdit}
                    className="flex-1 bg-[#E38B52] text-white py-3 rounded-2xl hover:bg-[#C8742F] hover:-translate-y-1 transition-all duration-200 font-medium shadow-[inset_0_2px_4px_rgba(255,255,255,0.3),inset_0_4px_8px_rgba(255,255,255,0.2)] flex items-center justify-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Save Changes
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="flex-1 bg-gray-500 text-white py-3 rounded-2xl hover:bg-gray-600 hover:-translate-y-1 transition-all duration-200 font-medium shadow-[inset_0_2px_4px_rgba(255,255,255,0.3),inset_0_4px_8px_rgba(255,255,255,0.2)]"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleEditToggle}
                    className="flex-1 bg-[#E38B52] text-white py-3 rounded-2xl hover:bg-[#C8742F] hover:-translate-y-1 transition-all duration-200 font-medium shadow-[inset_0_2px_4px_rgba(255,255,255,0.3),inset_0_4px_8px_rgba(255,255,255,0.2)] flex items-center justify-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                    Edit Details
                  </button>
                  <button className="flex-1 bg-white/30 backdrop-blur-xl rounded-2xl shadow-xl p-3 border border-white/20 hover:-translate-y-1 transition-all font-medium duration-200">
                    Download Profile
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Global styles for animations */}
      <style jsx global>{`
        @keyframes float {
          0% {
            transform: translate(0, 0) scale(1);
          }
          25% {
            transform: translate(100px, -100px) scale(1.2);
          }
          50% {
            transform: translate(0, 100px) scale(0.9);
          }
          75% {
            transform: translate(-100px, -50px) scale(1.1);
          }
          100% {
            transform: translate(0, 0) scale(1);
          }
        }
        .animate-float {
          animation: float 15s infinite ease-in-out;
        }
        .animation-delay-3000 {
          animation-delay: -5s;
        }
        .animation-delay-5000 {
          animation-delay: -10s;
        }
        .animation-delay-7000 {
          animation-delay: -15s;
        }
      `}</style>

      {/* Toast Notification */}
      {toast.show && (
        <div
          className={`fixed top-8 right-8 z-[9999] animate-slide-in-right ${
            toast.type === "success"
              ? "bg-green-500"
              : toast.type === "error"
              ? "bg-red-500"
              : "bg-blue-500"
          } text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 min-w-[320px] max-w-md`}
        >
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
            .animate-slide-in-right {
              animation: slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            }
          `}</style>
          <div className="flex-shrink-0">
            {toast.type === "success" ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>
          <div className="flex-grow font-semibold text-sm tracking-wide">
            {toast.message}
          </div>
          <button
            onClick={() => setToast({ show: false, message: "", type: "" })}
            className="flex-shrink-0 text-xl font-bold hover:text-white/80 transition-colors cursor-pointer"
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default TherapistPage;
